// Every detector, run over a walked archive.
//
// This is phase 3 of a scan (see `analyzeJar`). The walk decides what is in the
// sample; this decides what that means. It is one module because the detectors
// are not independent: the symbolic engine's reachability set is what makes an
// action reportable, the payload inspector reads entries the walk already
// decompressed, and the family signatures are matched against the same
// contexts the behaviour rules will be. Splitting them would mean reconstructing
// exactly this shared state in the caller.
//
// Order inside is load-bearing in one place only, and it is commented where it
// happens: the reachability closures are built before the detectors that use
// them, and YARA runs last because its targets are drawn from the values the
// earlier detectors contributed.
//
// Two counters belong to the walk but keep counting here, so they are returned
// rather than mutated in place. `stringId` continues the walk's string index so
// ids stay unique across the phase boundary, and `decodedValueCount` adds what
// the symbolic engine recovered on top of what the walk decoded. Both used to be
// shared `let` bindings visible to the whole function, which is how a reader
// lost track of who owned them.

import type JSZip from "jszip";
import { stringCategory } from "./archive-walk";

import type { AnalyzeOptions } from "./analyzer-options";
import { detectActions } from "./action-detector";
import { buildBehaviorChains } from "./behavior-chains";
import {
  matchSuspiciousWords,
  summarizeSuspiciousWords,
} from "./detection-dictionary";
import { buildFingerprints } from "./fingerprints";
import { assessJarIntegrity } from "./integrity";
import type { DetectionContext } from "./detection-rules";
import { ensureNotAborted, reportProgress } from "./scan-runtime";
import { analyzeSymbolicDataFlow } from "./symbolic-engine";
import {
  detectFamilySignatures,
  matchFingerprints,
} from "./threat-intel";
import { getYaraState, type YaraState, type YaraTarget, scanYara } from "./yara";
import { inspectEmbeddedPayloads, type PayloadCandidate } from "./payload-inspector";
import type {
  ArchiveEntry,
  ClassInfo,
  EntryHash,
  EntryPoint,
  ExtractedString,
  OfficialFileMatch,
  ScanProgress,
} from "./types";

// YARA scans every surface against every rule, so the surface count and size
// need budgets. Anything dropped is reported to the analyst, never hidden.
const MAX_YARA_TARGETS = 4_000;
const MAX_YARA_VALUE_LENGTH = 4_096;
const MAX_YARA_RECOVERED_BYTES = 64 * 1024;
const MAX_YARA_ENTRY_BYTES = 24 * 1024 * 1024;

export interface DetectorInput {
  /** The open container; the integrity checker reads signed digests from it. */
  archive: JSZip;
  analyzedBytes: Uint8Array;
  analyzedFileName: string;
  archiveEntries: ArchiveEntry[];
  classes: ClassInfo[];
  contexts: DetectionContext[];
  entryPoints: EntryPoint[];
  extractedStrings: ExtractedString[];
  entryHashes: EntryHash[];
  /** Entries the walk already decompressed; not inflated a second time. */
  hashedEntryBytes: Map<string, Uint8Array>;
  officialLookupPromise: Promise<OfficialFileMatch>;
  options: AnalyzeOptions;
  onProgress?: (progress: ScanProgress) => void;
  signal?: AbortSignal;
  /** Continues from the walk. */
  stringId: number;
  /** Continues from the walk; this phase adds the symbolic recoveries. */
  decodedValueCount: number;
}

export interface DetectorResult {
  fingerprints: ReturnType<typeof buildFingerprints>;
  fingerprintMatches: ReturnType<typeof matchFingerprints>;
  symbolic: ReturnType<typeof analyzeSymbolicDataFlow>;
  actions: ReturnType<typeof detectActions>;
  behaviorChains: ReturnType<typeof buildBehaviorChains>;
  suspiciousTerms: ReturnType<typeof summarizeSuspiciousWords>;
  hasReachabilityRoots: boolean;
  entryIsReachable: (entry: string) => boolean;
  referencedByReachableCode: (path: string) => boolean;
  embeddedPayloads: ReturnType<typeof inspectEmbeddedPayloads>;
  signatureFiles: string[];
  integrity: Awaited<ReturnType<typeof assessJarIntegrity>>;
  officialFile: OfficialFileMatch;
  familySignatureMatches: ReturnType<typeof detectFamilySignatures>;
  yaraState: YaraState;
  yaraWarnings: string[];
  yaraMatches: ReturnType<typeof scanYara>;
  /** The walk's counters after this phase added to them. */
  stringId: number;
  decodedValueCount: number;
}

export async function runDetectors(
  input: DetectorInput,
): Promise<DetectorResult> {
  const {
    archive,
    analyzedBytes,
    analyzedFileName,
    archiveEntries,
    classes,
    contexts,
    entryPoints,
    extractedStrings,
    entryHashes,
    hashedEntryBytes,
    officialLookupPromise,
    options,
    onProgress,
    signal,
  } = input;
  let { stringId, decodedValueCount } = input;
reportProgress(
  onProgress,
  "Tracing data flow",
  63,
  "Evaluating JVM stacks, call paths, and normalized fingerprints",
);
const fingerprints = buildFingerprints(classes);
// Structural near-duplicate lookup. The corpus is empty unless an analyst has
// installed a pack, so this is a no-op scan in the common case.
const fingerprintMatches = matchFingerprints(fingerprints.archive.simhash64);
reportProgress(
  onProgress,
  "Tracing data flow",
  64,
  "Resolving reachable methods and bounded interprocedural summaries",
);
const symbolic = analyzeSymbolicDataFlow(classes, entryPoints);
reportProgress(
  onProgress,
  "Tracing data flow",
  65,
  "Correlating reachable actions and end-to-end behavior chains",
);
decodedValueCount += symbolic.recovered.length;
const symbolicByEntry = new Map<string, string[]>();
for (const recovered of symbolic.recovered) {
  const values = symbolicByEntry.get(recovered.entry) ?? [];
  values.push(
    `[decoded symbolic ${recovered.transform} @ ${recovered.method}] ${recovered.value}`,
  );
  symbolicByEntry.set(recovered.entry, values);
  if (extractedStrings.length < 4_000) {
    const flags = matchSuspiciousWords(recovered.value);
    const category = stringCategory(recovered.value);
    extractedStrings.push({
      id: `str-${++stringId}`,
      value: recovered.value,
      entry: recovered.entry,
      category,
      suspicious:
        (category !== "class" && category !== "other") || flags.length > 0,
      flags,
      transform: `symbolic ${recovered.transform}`,
      method: recovered.method,
    });
  }
}
for (const [source, values] of symbolicByEntry) {
  contexts.push({ source, values });
}
const actions = detectActions(classes, contexts, {
  flows: symbolic.traces,
  reachableMethodKeys: symbolic.reachableMethodKeys,
  entryPointLabels: symbolic.entryPointLabels,
});
const behaviorChains = buildBehaviorChains(actions, symbolic.traces);
const suspiciousTerms = summarizeSuspiciousWords(extractedStrings);
const hasReachabilityRoots = entryPoints.length > 0;
const classByPath = new Map(classes.map((classInfo) => [classInfo.path, classInfo]));
const reachableMethodKeys = new Set(symbolic.reachableMethodKeys);
const entryPointClassNames = new Set(
  entryPoints.map((entryPoint) =>
    entryPoint.className.replaceAll("/", ".").replace(/::.*$/, ""),
  ),
);
const reachableClassPaths = new Set(
  classes
    .filter((classInfo) =>
      entryPointClassNames.has(classInfo.name) ||
      classInfo.methods.some((method) =>
        reachableMethodKeys.has(`${classInfo.name}#${method.name}`),
      ),
    )
    .map((classInfo) => classInfo.path),
);
const entryIsReachable = (source: string) =>
  !hasReachabilityRoots ||
  !classByPath.has(source) ||
  reachableClassPaths.has(source);
const reachableContextValues = contexts
  .filter((context) => entryIsReachable(context.source))
  .flatMap((context) => context.values);
const referencedByReachableCode = (path: string) => {
  if (!hasReachabilityRoots) return true;
  const fileName = path.split("/").pop() ?? path;
  return reachableContextValues.some(
    (value) => value.includes(path) || value.includes(fileName),
  );
};

reportProgress(
  onProgress,
  "Inspecting payloads",
  66,
  "Inspecting embedded executables and scripts without running them",
);
const hashByPath = new Map(entryHashes.map((entry) => [entry.path, entry]));
const payloadCandidates: PayloadCandidate[] = [];
for (const [path, bytes] of hashedEntryBytes) {
  const entryHash = hashByPath.get(path);
  if (!entryHash) continue;
  payloadCandidates.push({
    path,
    bytes,
    sha256: entryHash.sha256,
  });
}
const embeddedPayloads = inspectEmbeddedPayloads(payloadCandidates);

const signatureFiles = archiveEntries
  .filter((entry) => entry.kind === "signature")
  .map((entry) => entry.path);
reportProgress(
  onProgress,
  "Checking integrity",
  67,
  "Verifying signed-entry digests and archive coverage",
);
const integrity = await assessJarIntegrity(
  archive,
  archiveEntries,
  signatureFiles,
);
ensureNotAborted(signal);

reportProgress(
  onProgress,
  "Checking reputation",
  68,
  options.officialLookup
    ? "Checking the SHA-512 against official Modrinth releases"
    : "Online reputation lookup disabled",
);
const officialFile = await officialLookupPromise;
ensureNotAborted(signal);

const familySignatureMatches = detectFamilySignatures(
  contexts.filter((context) => entryIsReachable(context.source)),
);

// YARA runs over three surfaces: the analyzed bytes themselves, the
// decompressed content of each archive entry, and the values the static
// pipeline already extracted (decoded, constant-folded and otherwise
// recovered strings). The middle one is what most real rules are written
// against — a jar is a compressed container, so class and resource content
// is simply not present in the outer bytes, and a rule targeting class
// magic, a constant pool literal or a hex signature in a payload would
// never fire against the zip alone. A rule written for extracted text can
// only fire on the third surface, so an analyst needs all three.
const yaraState = getYaraState();
const yaraTargets: YaraTarget[] = [];
const yaraWarnings: string[] = [];
let yaraTargetOverflow = 0;
if (yaraState.rules.length) {
  yaraTargets.push({
    source: analyzedFileName,
    value: `${analyzedFileName} (file bytes)`,
    bytes: analyzedBytes,
    surface: "jar bytes",
  });
  const seenTargets = new Set<string>();
  // Decompressed entry content, reusing bytes the hashing pass already
  // decompressed. Budgeted by total bytes rather than entry count so a jar
  // of many huge resources cannot blow out the scan.
  let yaraEntryByteBudget = MAX_YARA_ENTRY_BYTES;
  for (const [path, bytes] of hashedEntryBytes) {
    if (bytes.byteLength > yaraEntryByteBudget) {
      yaraTargetOverflow += 1;
      continue;
    }
    yaraEntryByteBudget -= bytes.byteLength;
    yaraTargets.push({
      source: path,
      value: `${path} (entry content)`,
      bytes,
      surface: "entry content",
    });
  }
  // Extracted values are grouped per source, not emitted one target each.
  // YARA evaluates a rule against a single file, so `all of them` has to
  // mean "all present in this class" — emitting one target per value would
  // make that unsatisfiable and quietly under-report, which is the same
  // failure as a rule that silently matches nothing. Newlines separate the
  // values so a rule cannot match across a join it should not see.
  for (const context of contexts) {
    if (!entryIsReachable(context.source)) continue;
    const key = `${context.source}\u0000values`;
    if (seenTargets.has(key)) continue;
    seenTargets.add(key);
    const parts: string[] = [];
    let length = 0;
    let dropped = 0;
    for (const value of context.values) {
      if (
        value.length > MAX_YARA_VALUE_LENGTH ||
        length + value.length > MAX_YARA_RECOVERED_BYTES
      ) {
        dropped += 1;
        continue;
      }
      parts.push(value);
      length += value.length + 1;
    }
    yaraTargetOverflow += dropped;
    if (!parts.length) continue;
    yaraTargets.push({
      source: context.source,
      value: `${context.source} (extracted values)`,
      bytes: new TextEncoder().encode(parts.join("\n")),
      surface: "extracted values",
    });
  }
  if (yaraTargets.length > MAX_YARA_TARGETS) {
    yaraTargetOverflow += yaraTargets.length - MAX_YARA_TARGETS;
    yaraTargets.length = MAX_YARA_TARGETS;
  }
}
const yaraMatches = scanYara(yaraState.rules, yaraTargets, {
  trusted: yaraState.trusted,
});
if (yaraTargetOverflow) {
  yaraWarnings.push(
    `YARA searched ${MAX_YARA_TARGETS.toLocaleString()} surfaces and ${MAX_YARA_ENTRY_BYTES.toLocaleString()} bytes of decompressed entry content; ${yaraTargetOverflow.toLocaleString()} extracted value(s) or entry/entries were over budget or too large to search. Rules that would have matched them did not run.`,
  );
}
if (yaraState.unsupported.length) {
  yaraWarnings.push(
    `${yaraState.unsupported.length} YARA rule(s) could not run in this engine and did not contribute to the verdict. Open the YARA rules view for the specific construct in each.`,
  );
}

  return {
    fingerprints,
    fingerprintMatches,
    symbolic,
    actions,
    behaviorChains,
    suspiciousTerms,
    hasReachabilityRoots,
    entryIsReachable,
    referencedByReachableCode,
    embeddedPayloads,
    signatureFiles,
    integrity,
    officialFile,
    familySignatureMatches,
    yaraState,
    yaraWarnings,
    yaraMatches,
    stringId,
    decodedValueCount,
  };
}
